import { getMinioSignedUrl } from "@/functions/useMinioSignedUrl";
import type { FMFolderContentFileFragment, FMFolderContentFolderFragment } from "@/graphql";
import { LISA_DATE_FULL } from "@/language/translation";
import { FOLDER_MIME_TYPE, type FolderTableContent } from "../helper/FMFilePreviewHelper";
import { useTranslation } from "@/language/translation";
import { useFMFolderNames } from "../helper/FMFolderHelper";

export async function downloadFile(uuid?: string, fileName?: string, fileEnding?: string) {
  if (!uuid) return;

  const url = await getMinioSignedUrl(uuid);
  if (url == null) return;

  const response = await fetch(url);
  const blob = await response.blob();

  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = fileName
    ? `${fileName}${fileEnding ? `.${fileEnding}` : ".unknown"}`
    : "download.unknown";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(blobUrl);
}

export type FMFileForGallery = {
  fileUUID?: string;
  name: string;
  mimeType: string;
  hasThumbnail: boolean;
  createdAt: string;
};

export function useFolderTableContentFormat(onlyFile: true): {
  getFileFormat: (file: FMFolderContentFileFragment) => FolderTableContent;
  getFolderFormat: undefined;
  loading: undefined;
  getFolderStats: undefined;
};

export function useFolderTableContentFormat(onlyFile?: false | undefined): {
  getFileFormat: (file: FMFolderContentFileFragment) => FolderTableContent;
  getFolderFormat: (folder: FMFolderContentFolderFragment) => FolderTableContent;
  loading: ReturnType<typeof useFMFolderNames>["loading"];
  getFolderStats: ReturnType<typeof useFMFolderNames>["getFolderStats"];
};

export function useFolderTableContentFormat(onlyFile?: boolean) {
  const { t, d } = useTranslation();

  function getFileFormatInternal(file: FMFolderContentFileFragment): FolderTableContent {
    return {
      key: `File${file.id}`,
      mimeType: file.mimeType,
      name: file.fileName,
      folderId: file.folderId,
      fileUUID: file.fileUUID,
      fileId: file.id,
      hasThumbnail: file.hasThumbnail,
      createdAt: d(file.createdAt, LISA_DATE_FULL),
      updatedAt: d(file.updatedAt, LISA_DATE_FULL),
      createdBy: file.createdBy?.preferredName ?? t("global.deletedUser"),
      updatedBy: file.updatedBy?.preferredName ?? t("global.deletedUser"),
      createdAtISO: file.createdAt,
      folderType: undefined,
      leaf: true,
      originalFileEnding: file.originalFileEnding,
      folderPath: undefined,
    };
  }

  if (onlyFile) {
    return {
      getFileFormat: getFileFormatInternal,
      getFolderFormat: undefined,
      loading: undefined,
    };
  }

  const { getFolderStats, loading } = useFMFolderNames();

  function getFolderFormatInternal(folder: FMFolderContentFolderFragment): FolderTableContent {
    const folderStats = getFolderStats(folder);

    return {
      key: `Folder${folder.id}`,
      mimeType: FOLDER_MIME_TYPE,
      name: folderStats.name,
      fileUUID: undefined,
      folderId: folder.id,
      fileId: undefined,
      hasThumbnail: false,
      createdAt: d(folder.createdAt, LISA_DATE_FULL),
      updatedAt: d(folder.updatedAt, LISA_DATE_FULL),
      createdBy: folder.createdBy?.preferredName ?? t("global.deletedUser"),
      updatedBy: folder.updatedBy?.preferredName ?? t("global.deletedUser"),
      createdAtISO: folder.createdAt,
      folderType: folder.type,
      folderPath: folder.path,
      leaf: folder.isLeafFolder,
      siteId: folder.siteId,
    };
  }

  return {
    getFileFormat: getFileFormatInternal,
    getFolderFormat: getFolderFormatInternal,
    getFolderStats,
    loading,
  };
}
